'use strict';

const _ = require('lodash'),
    moment = require('moment'),
    {t} = require('services/i18nextWrapper'),
    ChronologycStore = require('stores/ChronologycStore'),
    BrewhouseStore = require('stores/BrewhouseStore'),
    ContentUtils = require('utils/ContentUtils'),
    DashboardServiceCache = require('services/DashboardServiceCache'),
    NumberUtils = require('utils/NumberUtils'),
    DateUtils = require('utils/DateUtils'),
    DashboardModalUtils = require('components/DashboardModal/DashboardModalUtils'),
    Notification = require('components/Notification/Notification'),
    Constants = require('constants/AppConstants');

const OrderUtils = {

    /**
     * Retrieve the amount of days of difference from old position to new position.
     * @param positionOld
     * @param positionNew
     * @return {number}
     */
    getVerticalOffeset(positionOld, positionNew) {
        if (positionOld && positionNew) {
            if (positionNew.y && positionOld.y) {
                return (+positionNew.y) - (+positionOld.y);
            } else {
                if (positionNew.isSingle) {
                    const positionOldY = ChronologycStore.getYFromDate(_.get(positionOld.fromDate));
                    const positionNewY = ChronologycStore.getYFromDate(_.get(positionNew.fromDate));
                    return positionNewY - positionOldY;
                } else {
                    const positionOldY = ChronologycStore.getYFromDate(_.get(positionOld.brewDate));
                    const positionNewY = ChronologycStore.getYFromDate(_.get(positionNew.brewDate));
                    return positionNewY - positionOldY;
                }
            }
        }
    },

    /**
     * Check all dates of the order if are properly assigned.
     * @param {Object} order the order to be validated
     */
    checkDateOfOrder(order) {

        if (_.get(order, 'isSingle')) {
            return true;
        }

        if (DateUtils.isEqual(_.get(order, 'brewDate'), _.get(order, 'filtrationDate'))) {
            Notification.show(t('dashboard_notification_brew_date_be_before_transfer_date'));
            return false;
        }

        if (DateUtils.isEqual(_.get(order, 'brewDate'), _.get(order, 'orderDate'))) {
            Notification.show(t('dashboard_notification_brew_date_must_be_different_as_packaging_date'));
            return false;
        }

        if (DateUtils.isAfter(_.get(order, 'brewDate'), _.get(order, 'filtrationDate'))) {
            Notification.show(t('dashboard_notification_brew_date_must_be_different_as_transfer_date'));
            return false;
        }

        if (DateUtils.isAfter(_.get(order, 'brewDate'), _.get(order, 'orderDate'))) {
            Notification.show(t('dashboard_notification_brew_date_must_be_before_packaging_date'));
            return false;
        }

        if (DateUtils.isBefore(_.get(order, 'filtrationDate'), _.get(order, 'brewDate'))) {
            Notification.show(t('dashboard_notification_transfer_date_must_be_after_brew_date'));
            return false;
        }

        if (DateUtils.isAfter(_.get(order, 'filtrationDate'), _.get(order, 'orderDate'))) {
            Notification.show(t('dashboard_notification_transfer_date_must_be_before_order_date'));
            return false;
        }

        if (DateUtils.isBefore(_.get(order, 'orderDate'), _.get(order, 'brewDate'))) {
            Notification.show(t('dashboard_notification_order_date_must_after_brew_date'));
            return false;
        }

        if (DateUtils.isBefore(_.get(order, 'orderDate'), _.get(order, 'filtrationDate'))) {
            Notification.show(t('dashboard_notification_packaging_date_must_be_same_or_after_transfer_date'));
        }

        return true;
    },

    isAlreadyPerformedGivenPosition(positionPerformedIds = [], position) {
        return _.includes(positionPerformedIds, position.i);
    },

    isDirectionTop(order) {
        var isDirectionTop;

        if (!order) {
            return null;
        }

        var tasksTarget = _.filter(_.union(order.packagingTasks, order.fermenterTasks), function (task) {
            return _.indexOf([Constants.BREW_TASK, Constants.FILTRATION_TASK, Constants.PACKAGING_TASK], task.type) > -1;
        });

        _.forEach(tasksTarget, function (task) {
            if (task.type === Constants.BREW_TASK && _.get(order, 'brewDate') !== task.date) {
                isDirectionTop = DateUtils.isBefore(_.get(order, 'brewDate'), task.date);
                return;
            }
            if (task.type === Constants.FILTRATION_TASK && _.get(order, 'filtrationDate') !== task.date) {
                isDirectionTop = DateUtils.isBefore(_.get(order, 'filtrationDate'), task.date);
                return;
            }
            if (task.type === Constants.PACKAGING_TASK && _.get(order, 'orderDate') !== task.date) {
                isDirectionTop = DateUtils.isBefore(_.get(order, 'orderDate'), task.date);
                return;
            }
        });

        return isDirectionTop;
    },

    getOrderAfterOrEqualDate(date, orders = DashboardServiceCache.getOrders()) {
        return _.filter(orders, order => {
            if (_.get(order, 'isSingle')) {
                return DateUtils.isAfterOrEquals(_.get(order, 'fromDate'), date);
            } else {
                return DateUtils.isAfterOrEquals(_.get(order, 'brewDate'), date);
            }
        });
    },

    updateOrder(orders = [], newOrder) {
        const filteredOrders = _.filter(orders, order => _.get(order,'id') !== _.get(newOrder, 'id')) || [];
        filteredOrders.push(newOrder);
        return filteredOrders;
    },

    getAllSortedTasksFromOrders(orders) {
        let allBrewingTasks = [];
        let allRemainderTasks = [];

        // get all task from all orders
        if (ContentUtils.isNotEmptyArray(orders)) {
            for (const order of orders) {
                const orderTasks = this.getTaskFromOrder(order);
                const allTasks = _.filter(orderTasks, task => task); // remove undefined positions
                const brewingTasks = _.filter(allTasks, task => task.turn); // filter all brewing task
                const remainderTasks = _.filter(allTasks, task => !task.turn); // filter all brewing task

                allBrewingTasks = allBrewingTasks.concat(brewingTasks);
                allRemainderTasks = allRemainderTasks.concat(remainderTasks);
            }
        }

        // filter task which have product and after to day
        const remainderTasksWithProducts = _.filter(allRemainderTasks, task => ContentUtils.isNotEmptyArray(_.get(task, 'products')));
        const brewingTaskWithProducts = _.filter(allBrewingTasks, task => ContentUtils.isNotEmptyArray(_.get(task, 'products')));
        const remainderSortedTasksWithProducts = DateUtils.sortListByDateField(remainderTasksWithProducts, 'date');
        const brewingSortedTaskWithProducts = DashboardModalUtils.sortBatchByColumAndDate(brewingTaskWithProducts);

        // to store which are scheduled and which are requested
        const allSortedTasks = _.concat(brewingSortedTaskWithProducts, remainderSortedTasksWithProducts);
        const allSortedTasksWithoutMissingInventoryItems = this.cleanAllWhereInventoryWasTakenNewItemMissingFromTasks(allSortedTasks);
        const allTaskSortedByDate = DateUtils.sortListByDateField(allSortedTasksWithoutMissingInventoryItems, 'date');

        return allTaskSortedByDate;
    },

    getAllItemsIdFromTasks(allTasks) {
        const allProducts = _.flatten(_.map(allTasks, task => task.products));
        const allItems = _.flatten(_.map(allProducts, product => product.items));
        return _.map(allItems, item =>  _.get(item, 'item.id') || _.get(item, 'itemId'));
    },

    getOrderAfterOrEqualDateFromEndDate(date, orders = DashboardServiceCache.getOrders()) {
        return _.filter(orders, order => {
            if (_.get(order, 'isSingle')) {
                return DateUtils.isAfterOrEquals(_.get(order, 'toDate'), date);
            } else {
                return DateUtils.isAfterOrEquals(_.get(order, 'orderDate'), date);
            }
        });
    },

    getOrderBeforeDate(date, orders = DashboardServiceCache.getOrders()) {
        return _.filter(orders, order => {
            if (_.get(order, 'isSingle')) {
                return DateUtils.isBefore(_.get(order, 'fromDate'), date);
            } else {
                return DateUtils.isBefore(_.get(order, 'brewDate'), date);
            }
        });
    },

    getOrderBeforeDateFromEndDate(date, orders = DashboardServiceCache.getOrders()) {
        return _.filter(orders, order => {
            if (_.get(order, 'isSingle')) {
                return DateUtils.isBefore(_.get(order, 'toDate'), date);
            } else {
                return DateUtils.isBefore(_.get(order, 'orderDate'), date);
            }
        });
    },

    getOrderByRange(from, to, orders = DashboardServiceCache.getOrders()) {
        const fromText = typeof from === 'string' ? from : DateUtils.toDateText(from);
        const toText = typeof from === 'string' ? to : DateUtils.toDateText(to);
        return _.filter(orders, order => {

            if (!order) {
                return false;
            }

            if (_.get(order, 'isSingle')) {

                if (DateUtils.isAfterOrEquals(_.get(order, 'toDate'), fromText) && DateUtils.isBeforeOrEquals(_.get(order, 'toDate'), toText)) {
                    return true;
                }

                if (DateUtils.isAfterOrEquals(_.get(order, 'fromDate'), fromText) && DateUtils.isBeforeOrEquals(_.get(order, 'fromDate'), toText)) {
                    return true;
                }

                if (DateUtils.isBeforeOrEquals(_.get(order, 'fromDate'), fromText) && DateUtils.isAfterOrEquals(_.get(order, 'toDate'), toText)) {
                    return true;
                }

            } else {

                if (DateUtils.isAfterOrEquals(_.get(order, 'orderDate'), fromText) && DateUtils.isBeforeOrEquals(_.get(order, 'orderDate'), toText)) {
                    return true;
                }

                if (DateUtils.isAfterOrEquals(_.get(order, 'brewDate'), fromText) && DateUtils.isBeforeOrEquals(_.get(order, 'brewDate'), toText)) {
                    return true;
                }

                if (DateUtils.isBeforeOrEquals(_.get(order, 'brewDate'), fromText) && DateUtils.isAfterOrEquals(_.get(order, 'orderDate'), toText)) {
                    return true;
                }

            }
        });
    },

    isOrderPartiallyShown(dashboardFrom, dashboardTo, orderPosition) {
        if (orderPosition && orderPosition.order) {
            var orderTarget = orderPosition.order;
            if (ContentUtils.isBT(orderPosition.position.i)) {
                return DateUtils.isBefore(orderTarget.filtrationDate, dashboardFrom) || DateUtils.isAfter(orderTarget.orderDate, dashboardTo);
            } else {
                return DateUtils.isBefore(orderTarget.brewDate, dashboardFrom) || DateUtils.isAfter(orderTarget.filtrationDate, dashboardTo);
            }
        }
        return false;
    },

    convertOrderToPositions(order, fromDashboard, customSinglePosition, forceBatchAsRelocated = false) {
        let positions = [];

        if (_.get(order, 'isSingle')) {
            positions = [
                this.toSingleTankPosition(order, fromDashboard, customSinglePosition)
            ];
        } else {
            positions = [
                this.toBriteTankPosition(order, fromDashboard),
                this.toFermenterTankPosition(order, fromDashboard)
            ];
        }
        // add brewhouse batch positions
        if (_.get(order, 'batchs.length', 0) > 0) {
            const batchs = _.get(order, 'batchs', []);
            for (const batch of batchs) {
                const batchPosition = _.get(BrewhouseStore.toBatchPosition(order, batch), 'position');
                if (forceBatchAsRelocated) {
                    batchPosition.isRelocated = true;
                }
                if (batchPosition) {
                    positions.push(batchPosition);
                }
            }
        }

        return positions;
    },

    toSingleTankPosition(order, dashboardFrom, customPosition) {
        let column, height, positionY, id;

        if (customPosition) {
            column = _.get(customPosition, 'x');
            height = _.get(customPosition, 'h');
            positionY = _.get(customPosition, 'y');
            id = _.get(customPosition, 'i');
        } else {
            column = _.get(order, 'singleTank.column');
            height = ChronologycStore.getHFromDate(_.get(order, 'fromDate'), _.get(order, 'toDate'));
            positionY = ChronologycStore.getYFromDate(_.get(order, 'fromDate'), dashboardFrom);
            const tankTypeConfig = ContentUtils.getTankTypeConfiguration(_.get(order, 'singleTank.type'));
            id = order.id + _.get(tankTypeConfig, 'postFix');
        }
        // only for singles, when add single order, we need to show not te single tak.
        return {
            h: height,
            i: id,
            static: false,
            w: 1,
            x: column,
            y: positionY,
            batchNumber: _.get(order, 'batchNumber'),
            isSingle: _.get(order, 'isSingle'),
            volume: _.get(order, 'volume')
        };
    },

    toBriteTankPosition(order, dashboardFrom) {
        return {
            h: ChronologycStore.getHFromDate(_.get(order, 'filtrationDate'), _.get(order, 'orderDate')),
            i: order.id + '_BT',
            static: false,
            w: 1,
            x: _.get(order, 'briteTank.column'),
            y: ChronologycStore.getYFromDate(_.get(order, 'filtrationDate'), dashboardFrom),
            batchNumber: _.get(order, 'batchNumber'),
            volume: _.get(order, 'volume'),
            isSingle: _.get(order, 'isSingle')
        };
    },

    toFermenterTankPosition(order, dashboardFrom) {
        return {
            h: ChronologycStore.getHFromDate(_.get(order, 'brewDate'), _.get(order, 'filtrationDate')),
            i: order.id,
            static: false,
            w: 1,
            x: _.get(order, 'fermenterTank.column'),
            y: ChronologycStore.getYFromDate(_.get(order, 'brewDate'), dashboardFrom),
            batchNumber: _.get(order, 'batchNumber'),
            volume: _.get(order, 'volume'),
            isSingle: _.get(order, 'isSingle')
        };
    },

    toBatchPosition(order, dashboardFrom) {
        return {
            h: ChronologycStore.getHFromDate(_.get(order, 'brewDate'), _.get(order, 'filtrationDate')),
            i: order.id,
            static: false,
            w: 1,
            x: _.get(order, 'fermenterTank.column'),
            y: ChronologycStore.getYFromDate(_.get(order, 'brewDate'), dashboardFrom),
            batchNumber: _.get(order, 'batchNumber'),
            isSingle: _.get(order, 'isSingle')
        };
    },

    updateDates(order, offsetY) {

        if (!order) {
            return order;
        }

        const isSingle = _.get(order, 'isSingle');

        if (isSingle) {
            order.fromDate = DateUtils.addDaysTextBasicFormat(DateUtils.toDate(_.get(order, 'fromDate')), offsetY);
            order.toDate = DateUtils.addDaysTextBasicFormat(DateUtils.toDate(_.get(order, 'toDate')), offsetY);
        } else {
            order.brewDate = DateUtils.addDaysTextBasicFormat(DateUtils.toDate(_.get(order, 'brewDate')), offsetY);
            order.filtrationDate = DateUtils.addDaysTextBasicFormat(DateUtils.toDate(_.get(order, 'filtrationDate')), offsetY);
            order.orderDate = DateUtils.addDaysTextBasicFormat(DateUtils.toDate(_.get(order, 'orderDate')), offsetY);
        }

        return order;
    },

    checkIfTankHaveEnoughVolume(positionNew) {

        if (ContentUtils.isBHBatch(positionNew.i)) {
            return true;
        }

        const order = this.getOrder(ContentUtils.cleanId(positionNew.i));
        const orderVolume = +_.get(order, 'volume', 0);

        if (orderVolume > 0 && !BrewhouseStore.isProperlyVolumeOfTankDestination(positionNew, orderVolume)) {
            return false;
        }

        return true;
    },


    isOutOfRangeNextTaskPosition(order, nextDateClickedText, isSingle, tankType) {
        let fromDate, toDate;

        if (isSingle) {
            fromDate = _.get(order, 'fromDate');
            toDate = _.get(order, 'toDate');
        } else {
            if (tankType === 'FV') {
                fromDate = _.get(order, 'brewDate');
                toDate = _.get(order, 'filtrationDate');
            } else if (tankType === 'BT') {
                fromDate = _.get(order, 'filtrationDate');
                toDate = _.get(order, 'orderDate');
            }
        }
        if (!DateUtils.isDateBetween(nextDateClickedText, fromDate, toDate)) {
            Notification.show(t('dashboard_notification_task_could_not_move_outside'));
            return false;
        }

        return true;
    },

    /**
     * Check and Update types if need of given combined order.
     * NOTE: single orders, don't have spacial task type, so this logic apply only to combined orders.
     * @param order
     * @param task
     * @private
     */
    updateTaskType(order, task) {
        if (order && !order.isSingle) {
            if (_.get(order, 'brewDate') === task.date) {
                task.type = Constants.BREW_TASK;
            } else if (_.get(order, 'filtrationDate') === task.date) {
                task.type = Constants.FILTRATION_TASK;
            } else if (_.get(order, 'orderDate') === task.date) {
                task.type = Constants.PACKAGING_TASK;
            } else {
                task.type = Constants.SECONDARY_TASK;
            }
        }
        return task;
    },

    filterUnneededPositions(positionsPerformed) {
        // remover ordenes duplicadas con post fijos duplicados
        let orderFilteredId = [];
        let positionToSave = [];

        for (const position of positionsPerformed) {
            const id = ContentUtils.cleanId(position.newPosition.i);
            if (_.includes(orderFilteredId, id) || ContentUtils.isBHBatch(position.newPosition.i)) {
                // is already processed or is brew house batch so is not need to be processed
                continue;
            } else {
                orderFilteredId.push(id);
                positionToSave.push(position);
            }
        }

        return positionToSave;
    },

    /**
     * Retrieve order by Id.
     * @param orderId
     * @returns {*}
     */
    getOrder(orderId) {
        return DashboardServiceCache.getOrder(orderId);
    },

    electBatchAvailable(batchsAvailable, oldBatch) {
        let batchElected = _.find(batchsAvailable, batch => _.get(batch, 'position.x') === _.get(oldBatch, 'x'));
        if (batchElected) {
            return batchElected;
        } else {
            batchElected = _.first(batchsAvailable);
            return batchElected;
        }
    },

    getTaskFromOrder(order) {
        if (!order) {
            return order;
        }
        if (_.get(order, 'isSingle')) {
            return _.filter(_.concat(order.singleTasks, order.batchs), task => task);
        } else {
            return _.filter(_.concat(order.batchs, order.fermenterTasks, order.packagingTasks), task => task);
        }
    },
    
    getTaskFromOrderBySectionAndDate(order, sectionType, date) {
        if (!order) {
            return order;
        }
        let filteredTasks = [];
        if (_.get(order, 'isSingle')) {
            filteredTasks = _.filter(order.singleTasks, task => (task.sectionType || task.tankType) === sectionType && DateUtils.toDateText(date) === task.date);
        } else {
            if (sectionType === 'FV') {
                filteredTasks = _.filter(order.fermenterTasks, task => task.date === DateUtils.toDateText(date));
            } else if (sectionType === 'BT') {
                filteredTasks = _.filter(order.packagingTasks, task => task.date === DateUtils.toDateText(date));
            } else {
                filteredTasks = _.filter(order.batchs, task => task.date === DateUtils.toDateText(date));
            }
        }
        return _.filter(filteredTasks, task => task);
    },

    recalculateOffsetProductsEndDate(task, offesetDays) {
        const products = this.hydrateEndDateProduct(task, _.get(task, 'products'));

        if (ContentUtils.isNotEmptyArray(products)) {
            const productsUpdated = _.map(products, product => {
                product.endDate = moment((product.endDate || task.date)).add(offesetDays, 'days').format(Constants.DATE_FORMAT_LONG);
                return product;
            });
            task.products = productsUpdated;
        }

        return task;
    },

    getAllProductsFromOrder(order) {
        const tasks = this.getTaskFromOrder(order);
        return _.flatMap(tasks, task => this.hydrateEndDateProduct(task, _.get(task, 'products')) || []);
    },
    
    hydrateEndDateProduct(task, products) {
        if (!task || !products || products.length === 0) return products;
        return (products || []).map(product => {
            product.endDate = task.date;
            return product;
        });
    },
    
    getAllProductsFromOrderBySectionAndDate(order, sectionType, date) {
        const tasks = this.getTaskFromOrderBySectionAndDate(order, sectionType, date);
        return _.flatMap(tasks, task => this.hydrateEndDateProduct(task, _.get(task, 'products')) || []);
    },

    getAllWhereInventoryWasTakenFromAllItemsFromAllProductsFromOrders(orders, minDate) {
        const whereInventoryWasTaken = [];
        for (const order of orders) {
            const products = this.getAllProductsFromOrder(order);
            const filteredProducts = products.filter(product => {
                const productDate = DateUtils.toDateText(product.endDate);
                return DateUtils.isAfter(productDate, minDate);
            });
            for (const product of filteredProducts) {
                for (const item of product.items) {
                   if (item.whereInventoryWasTakenNew && item.whereInventoryWasTakenNew.length) {
                       whereInventoryWasTaken.push(item.whereInventoryWasTakenNew);
                   }
                   if (item.whereInventoryWasTakenSave && item.whereInventoryWasTakenSave.length) {
                       whereInventoryWasTaken.push(item.whereInventoryWasTakenSave);
                   }
                }
            }
        }
        return _.flatMap(whereInventoryWasTaken);
    },

    getAllProductsFromTasks(tasks) {
        return _.flatMap(tasks, task => this.hydrateEndDateProduct(task, _.get(task, 'products')) || []) || [];
    },

    getAllFermentingProductsFromOrder(order) {
        return this.getAllProductsFromTasks(order.fermenterTasks);
    },

    getAllPackagingProductsFromOrder(order) {
        return this.getAllProductsFromTasks(order.packagingTasks);
    },

    getAllBrewingProductsFromOrder(order) {
        return this.getAllProductsFromTasks(order.batchs);
    },

    haveSomeProductConfirmed(tasks) {
        return _.some(tasks, task =>
            _.some(
                _.get(task, 'products'), product => _.get(product, 'isConfirmed')));
    },

    haveSomeProductConfirmedFromOrder(order) {
        const tasks = this.getTaskFromOrder(order);
        return _.some(tasks, task =>
            _.some(
                _.get(task, 'products'),
                product => _.get(product, 'isConfirmed')));
    },
    
    haveSomeProductConfirmedFromOrderAfterDate(order, dateClicked) {
        const products = this.getAllProductsFromOrder(order);
        return _.some(products, product =>
          DateUtils.isAfter(DateUtils.toDateText(product.endDate), DateUtils.toDateText(dateClicked)) &&
          _.get(product, 'isConfirmed')
        );
    },
    
    haveSomeProductConfirmedFromOrderAfterOrEqualDate(order, dateClicked) {
        const products = this.getAllProductsFromOrder(order);
        return _.some(products, product =>
          (DateUtils.isEqual(DateUtils.toDateText(product.endDate), DateUtils.toDateText(dateClicked)) ||
            DateUtils.isAfter(DateUtils.toDateText(product.endDate), DateUtils.toDateText(dateClicked)))
          && _.get(product, 'isConfirmed')
        );
    },
    
    
    isAbleToMoveTaskADayBefore(order, dateClicked, tankClicked) {
        return (
           this.haveSomeProductConfirmedFromOrderByDate(order, dateClicked, tankClicked) === false &&
             // Notice we should check a day before, in order to avoid to do an ENROQUE, to some confirmed product
           this.haveSomeProductConfirmedFromOrderByDate(order, DateUtils.addDays(dateClicked, -1), tankClicked) === false
        );
    },
    
    isAbleToMoveTaskADayAfter(order, dateClicked, tankClicked) {
        return (
           this.haveSomeProductConfirmedFromOrderByDate(order, dateClicked, tankClicked) === false &&
           // Notice we should check a day after, in order to avoid to do an ENROQUE, to some confirmed product
           this.haveSomeProductConfirmedFromOrderByDate(order, DateUtils.addDays(dateClicked, 1), tankClicked) === false
        );
    },
    
    haveSomeProductConfirmedFromOrders(orders) {
        return _.some(orders, order => this.haveSomeProductConfirmedFromOrder(order));
    },

    haveSomeProductConfirmedFromButchNumber(batchNumber) {
        const orders = DashboardServiceCache.getOrdersByBatchNumber(batchNumber);
        return this.haveSomeProductConfirmedFromOrders(orders);
    },

    haveSomeProductConfirmedFromOrderId(orderId) {
        const order = DashboardServiceCache.getOrder(orderId);
        return this.haveSomeProductConfirmedFromOrder(order);
    },
    
    haveSomeProductConfirmedByPosition(position) {
        const orderId = ContentUtils.cleanId(position.i);
        const taskInfo = ChronologycStore.getDateFromPositionY(position.y);
        const order = DashboardServiceCache.getOrder(orderId);
        const tasks = OrderUtils.getTaskFromOrderBySectionAndDate(order, 'BH', taskInfo.date);
        const taskByColumn = tasks.find(task => _.get(task, 'turn.column') === position.x);
        const products = (taskByColumn || {products: []}).products;
        return _.some(products, product => product && product.isConfirmed);
    },
    
    haveSomeProductConfirmedFromOrderByDate(order, date, tankClicked) {
        const products = this.getAllProductsFromOrderBySectionAndDate(order, tankClicked.type, date);
        return _.some(products, product => (product.isConfirmed || product.isDiscounted));
    },
    
    setDefaultEndHourToProductsOfTask(task) {
        task.products = this.hydrateEndDateProduct(task, _.get(task, 'products'));
        return task;
    },

    overrideDefaultEndHourToProductsOfTask(task) {
        task.products = this.hydrateEndDateProduct(task, _.get(task, 'products'));
        return task;
    },

    isChangedOrderAlreadyStarted(orderId) {
        const order = DashboardServiceCache.getOrder(orderId);
        if (order) {
            const isSingle = _.get(order, 'isSingle');
            let fromDate, toDate;
            if (isSingle) {
                fromDate = _.get(order, 'fromDate');
                toDate = _.get(order, 'toDate');
            } else {
                fromDate = _.get(order, 'brewDate');
                toDate = _.get(order, 'orderDate');
            }
            const isAfterToDay = DateUtils.isBeforeToDay(fromDate) || DateUtils.isBeforeToDay(toDate);

            if (isAfterToDay) {
                return true;
            }
        }
        return false;
    },
    
    recalculateProcessDate(task) {
        if (ContentUtils.isNotEmptyArray(task.process)) {
            task.process = task.process.map(process => {
                if (process.fromDate) {
                    process.fromDate = task.date;
                }
                
                if (process.toDate) {
                    process.toDate =  task.date;
                }
                
                process = this._updateProcessTaskIdDate(process, task.date);
                
                return process;
            });
        }
        return task;
    },
    
    _updateProcessTaskIdDate(process, newDateTask) {
        if ('BREWHOUSE_TASK' === process.processSection) {
            // in case that brewing, don't need to update any prefix date
            return process;
        }
        const oldTaskId = process.taskId;
        const oldProcessPostFix = oldTaskId.substring(oldTaskId.indexOf('_'), oldTaskId.length);
        process.taskId = `${newDateTask}${oldProcessPostFix}`;
        return process;
    },
    
    isResizeAllowed(startDate, endDate, toDay) {
        const isStartDateAfterToDay = DateUtils.isAfterOrEquals(startDate, toDay);
        const isEndDateAfterToDay = DateUtils.isAfterOrEquals(endDate, toDay);

        return isStartDateAfterToDay && isEndDateAfterToDay;
    },

    isAbleToConfirmSomeProduct(order, dateTask) {
        const allTasks = this.getTaskFromOrder(order);
        if (this.isAllProductAlreadyConfirmed(allTasks, dateTask)) {
            return true;
        }

        const allBeforeTasksWithSomeProduct = _.filter(allTasks, task =>
            ContentUtils.isNotEmptyArray(_.get(task, 'products')) &&
            _.some(_.get(task, 'products'), product => NumberUtils.isGreater(_.get(product, 'amount'), '0')) &&
            DateUtils.isBefore(_.get(task, 'date'), dateTask));

        const allTasksHaveAtLeastSomeConfirmedProduct = _.every(allBeforeTasksWithSomeProduct, task => {
            const products = _.get(task, 'products');
            return _.some(products, product => _.get(product, 'isConfirmed'));
        });

        return allTasksHaveAtLeastSomeConfirmedProduct;
    },

    isAllProductAlreadyConfirmed(allTasks, dateTask) {
        const filteredTasks = _.filter(allTasks, task => task.date === dateTask);
        return _.every(filteredTasks, task => _.every(_.get(task, 'products'), product => product.isConfirmed));
    },

    cleanAllWhereInventoryWasTakenNewItemMissingFromTasks(tasks) {
        return _.map(tasks, task => {
            const products = _.get(task, 'products');
            task.products = _.map(products, product => {
                const items = _.get(product, 'items');
                product.items = _.map(items, item => {
                    if (item && ContentUtils.isNotEmptyArray(item.whereInventoryWasTakenNew)) {
                        item.whereInventoryWasTakenNew = _.filter(item.whereInventoryWasTakenNew, itemInfo => _.get(itemInfo, 'type') !== 'VENDOR_PURCHASE_ITEM_MISSING_MODULE');
                    }
                    return item;
                });
                return product;
            });
            return task;
        });
    },
    
    cleanAllWhereInventoryWasTakenByProduct(product) {
        const items = _.get(product, 'items');
        product.items = _.map(items, item => {
            if (item && ContentUtils.isNotEmptyArray(item.whereInventoryWasTakenNew)) {
                item.whereInventoryWasTakenNew = [];
            }
            return item;
        });
        return product;
    },

    replaceById(orders, newOrder) {
        return ContentUtils.replaceByAttributes(orders, newOrder, 'id');
    },

    hasOneDayPackagingSide(order) {
        if (!_.get(order, 'filtrationDate') || !_.get(order, 'orderDate')) {
            return false;
        }

        return DateUtils.getDaysBetween(_.get(order, 'filtrationDate'), _.get(order, 'orderDate')) === 0;
    },

    hasOneDayFermenterSide(order) {
        if (!_.get(order, 'brewDate') || !_.get(order, 'filtrationDate')) {
            return false;
        }

        return DateUtils.getDaysBetween(_.get(order, 'brewDate'), _.get(order, 'filtrationDate')) === 0;
    },

    hasOneDaySingleOrder(order) {
        if (!_.get(order, 'fromDate') || !_.get(order, 'toDate')) {
            return false;
        }

        return DateUtils.getDaysBetween(_.get(order, 'fromDate'), _.get(order, 'toDate')) === 0;
    },

    getEndDateOrder(order) {
        if (order && order.isSingle) {
            return order.toDate;
        } else {
            return order.orderDate;
        }
    },

    getStartDateOrder(order) {
        if (order && order.isSingle) {
            return order.fromDate;
        } else {
            return order.brewDate;
        }
    },
    
    isOrderInRange(dashboardFrom, dashboardTo, order) {
        const orderFrom = this.getStartDateOrder(order);
        const orderEnd = this.getEndDateOrder(order);
        
        return DateUtils.isAfterOrEquals(orderFrom, dashboardFrom) && DateUtils.isBeforeOrEquals(orderEnd, dashboardTo);
    },

     toggleConfirmProduct(product) {
        if (product) {
            product.isConfirmed = !product.isConfirmed;
            if (!product.isConfirmed && product.isDiscounted) {
                product.needToPublishDisconfirmation = true;
            } else {
                product.needToPublishDisconfirmation = false;
            }
        }

        return product;
    },
     
    hasSomeProductBeforeTodayAndBefore(order, from) {
        const taskOutRangeBefore = _.filter(
                this.getAllSortedTasksFromOrders([order]),
                (task) =>
                    DateUtils.isBeforeToDay(task.date) &&
                    DateUtils.isBefore(task.date, from)
            );

            let someItemsBeforeNotConfirmed = false;

            taskOutRangeBefore.forEach(task => {
                if(task){
                    task.products.forEach((p)=>{
                        if(!p.isConfirmed){
                            someItemsBeforeNotConfirmed = true;
                        }
                    });
                }
                
            });

            const substractedItems = [];

            if(someItemsBeforeNotConfirmed){
                substractedItems.push(this.getAllProductsFromTasks(
                    taskOutRangeBefore
                ).filter((p) => {
                    return p.items.filter((i) => !i.addition);
                }));
            }

        return substractedItems.length > 0;
    },

    getCandidateProductToConfirm(orders, from, to) {
        let candidateProducts = [];

        for (const order of orders) {
            if (this.hasSomeProductBeforeTodayAndBefore(order, from)) {
                // if there some product outside of the range of the order, should not allow to confirm the rest.
                continue;
            } else {
                const tasks = _.filter(
                    this.getAllSortedTasksFromOrders([order]),
                    (task) =>
                        DateUtils.isBeforeToDay(task.date) &&
                        DateUtils.isAfterOrEquals(task.date, from) &&
                        DateUtils.isBeforeOrEquals(task.date, to)
                );
                const products = this.getAllProductsFromTasks(tasks);
                const productsCandidateToConfirm = products.filter((p) => {
                    const substractedItems = p.items.filter((i) => !i.addition);
                    return substractedItems.every((si) => !!si.isEnoughInventory);
                });
                 candidateProducts.push(productsCandidateToConfirm);
            }
        }
     
        return candidateProducts.flat();
    },

     
    confirmAllProductsByOrder(order, from, to) {
        if (this.hasSomeProductBeforeTodayAndBefore(order, from)) {
            // enter here means have some products before today and before range, so 
            // we should not allow to confirm any product in the range.
            return;
        }

       let wasConfirmedSomeProduct = false;
        
       const candidateProductToConfirm = this.getCandidateProductToConfirm(
           [order],
           from,
           to
       );

        for (const product of candidateProductToConfirm) {
            this.toggleConfirmProduct(product); // changing the product reference of given task
            wasConfirmedSomeProduct = true;
        }
        return wasConfirmedSomeProduct; // return the order with the confirmed products
    }
    
};

   

module.exports = OrderUtils;



/** WEBPACK FOOTER **
 ** ./src/utils/OrderUtils.js
 **/